import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { getChildrenForSlot } from '../../global/ui-helpers';
import type { ElementConfig } from '../ui-element.type';
import styles from './ui-title.css';
import { browser } from '../../global/index';
import { UIBadge } from '../badge/ui-badge';
import type { BrowserCallback } from '../../global/browser';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UITitle
 * @element ui-title
 * @classdesc Represents a page title component with responsive container behavior.
 * The container spans full viewport width while content respects maximum widths per device.
 * @property {string} [text] {@attr text} The page title text displayed as h1.
 * @slot {@name badge} Optional badge displayed on the right side of the page. Supports only segment layout.
 * @example
 * <ui-title text="Welcome to Swedbank!"></ui-title>
 * @example
 * <ui-title text="My name">
 *   <ui-badge slot="badge" color="orange" layout="segment">Gold Customer</ui-badge>
 * </ui-title>
 */

@Component({
    tag: 'ui-title',
    styles,
})
export class UITitle extends UIElement {
    @Attribute(String) text: string = '';
    @Child('.ui-title__badge > [layout]') badge!: UIBadge;

    private removeMobileMediaHandler: BrowserCallback | null = null;

    /**
     * Sets up responsive badge layout switching.
     * Only handles segment badges, skips non-segments.
     * @private
     */
    private setupResponsiveBadge(): void {
        if (!this.badge) {
            return;
        }

        const updateLayout = (): void => {
            this.badge?.setAttribute(
                'layout',
                browser.mobileMediaMatches()
                    ? UIBadge.Layout.SegmentSmall
                    : UIBadge.Layout.Segment
            );
        };

        updateLayout();
        this.removeMobileMediaHandler = browser.addMediaQueryChangeHandler(
            browser.mobileMedia,
            [updateLayout]
        );
    }

    /**
     * Cleans up media query listener.
     * @private
     */
    private cleanupResponsiveBadge(): void {
        this.removeMobileMediaHandler?.();
        this.removeMobileMediaHandler = null;
    }

    /**
     * @inheritDoc
     */
    render() {
        const [firstBadgeElement] = getChildrenForSlot(this, 'badge');
        const badgeWrapper: ElementConfig | null = firstBadgeElement
            ? {
                  tagName: 'div',
                  classList: { 'ui-title__badge': true },
                  children: [firstBadgeElement],
              }
            : null;

        this.insertElements([
            {
                tagName: 'div',
                classList: {
                    'ui-title__container': true,
                },

                children: [
                    {
                        tagName: 'div',
                        classList: {
                            'ui-title__content': true,
                        },
                        children: [
                            {
                                tagName: 'h1',
                                classList: { 'ui-title__text': true },
                                children: [this.text],
                            },
                            ...(badgeWrapper ? [badgeWrapper] : []),
                        ],
                    },
                ],
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.setupResponsiveBadge();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.cleanupResponsiveBadge();
    }
}
